import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import { DATE_FORMAT } from 'utilities/constants';
import { getCardAccountDefaultRequest, resetCardAccountDefault } from 'actions/cardActions';
import { getCardOptionsAction, getCardOptionsReset, getVehicleTypesAction } from 'actions/storeActions';
import { getPlasticType, resetPlasticType } from 'actions/maintenanceActions';
import {
  getEditCardDetails,
  getFleetLimiterCodesObj,
  vasClassifierMapping,
} from 'features/cards/helpers/add-edit-card-helper';
import Vas from 'constants/vas-codes';
import isEmpty from 'utilities/isEmpty';
import type {
  FleetLimiter,
  CardDefaultsResponse,
} from 'features/cards/components/new-card-modal/types/CardDefaultsResponseTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

type Account = {
  contractNumber: string;
};

type CardAccountDefault = {
  vasRsaAccountCode?: string;
  vasSnrAccountCode?: string;
  vasFuelPayAccountCode?: string;
  vasServicesAndRepairsFee?: unknown;
  vasRoadSideAssistFeeHeavy?: unknown;
  vasRoadSideAssistFeePassenger?: unknown;
  vasRoadSideAssistFeeTruck?: unknown;
};

export type CardDetail = {
  cardNumber?: string;
  contractNumber?: string;
  cardStatus?: string;
  fleetLimiters?: FleetLimiter[];
  expiryDate?: string;
  monthlyDollarLimits?: unknown[];
  dailyDollarLimits?: { code: string | number }[];
  accountDefault?: CardAccountDefault;
};

type CardOption = {
  id: string;
  favouriteName: string;
};

type PlasticType = {
  id: string;
};

/** Opaque form-values bag built by spreading multiple API objects. */
type CardFormValues = Record<string, unknown>;

type UseAddEditCardDefaultsParams = {
  cardDetails: CardDetail;
  allAccounts: Account[];
  accountDefaults: Partial<CardDefaultsResponse>;
  cardOptions: CardOption[];
  cardOptionsAccountNumber: string;
  vehicleTypes: object[];
  plasticType: PlasticType | null;
  formType: 'add' | 'edit';
  formId: string;
  isEditMultipleCard?: boolean;
  assignedGrandfatherClientNumber: string;
  maxExpiryMonths: number;
  setModifiedCardDetails?: (_values: CardFormValues) => void;
};

/**
 * useAddEditCardDefaults
 *
 * Manages all default-value initialisation for the AddEditCardForm, including:
 *  - Fleet limiter refs and card options ref
 *  - VAS code state (RSA, SNR, FuelPay)
 *  - Form default values (populated for add mode from account defaults,
 *    and for edit mode from the selected card's details)
 *  - Account favourites dropdown data
 *  - handleAccountSelection — dispatches account-default and card-option fetches
 *    when the user selects an account in add mode
 *
 * Extracted from AddEditCardForm to separate data initialisation from rendering.
 */
export const useAddEditCardDefaults = ({
  cardDetails,
  allAccounts,
  accountDefaults,
  cardOptions,
  cardOptionsAccountNumber,
  vehicleTypes,
  plasticType,
  formType,
  formId,
  isEditMultipleCard,
  assignedGrandfatherClientNumber,
  maxExpiryMonths,
  setModifiedCardDetails,
}: UseAddEditCardDefaultsParams) => {
  const dispatch = useDispatch();

  // setModifiedCardDetails is an optional callback from the parent that may be recreated on
  // every render. Storing in a ref prevents it from invalidating effect dep arrays.
  // Assigned synchronously during render (not in an Effect) — the ref is only ever read
  // inside Effects and event handlers, which run after render, so this is safe.
  const setModifiedCardDetailsRef = useRef(setModifiedCardDetails);
  setModifiedCardDetailsRef.current = setModifiedCardDetails;

  // Redux values can read as undefined inside handleChange closure — kept as refs.
  // TODO Investigate if there is a better solution
  const fleetLimitersRef = useRef<FleetLimiter[] | undefined>(undefined);
  const cardOptionsRef = useRef(cardOptions);
  const isMounted = useRef(false);

  const [vasRsaAccountCode, setVasRsaAccountCode] = useState<string | undefined>();
  const [vasSnrAccountCode, setVasSnrAccountCode] = useState<string | undefined>();
  const [vasFuelPayAccountCode, setVasFuelPayAccountCode] = useState<string | undefined>();
  const [defaultValues, setDefaultValues] = useState<CardFormValues>({});

  const accountFavouritesData = useMemo(
    () => (cardOptions ?? []).map((item) => ({ label: item.favouriteName, value: item.id })),
    [cardOptions]
  );

  const handleAccountSelection = (contractNumber: string) => {
    dispatch(
      getCardAccountDefaultRequest({
        contractNumber,
        grandParentAccountNumber: assignedGrandfatherClientNumber,
      })
    );
    dispatch(getCardOptionsAction(contractNumber, assignedGrandfatherClientNumber));
  };

  // Actions on mount or when page changes in edit-multiple-card flow.
  // The unusual dependency `isEditMultipleCard ? cardDetails : undefined` is intentional:
  // in multi-edit mode, this effect re-runs each time the user navigates to a new card.
  // In single-edit mode, this effect is only needed once (on mount).
  useEffect(() => {
    if (formType === 'edit' && cardDetails?.cardNumber !== defaultValues?.cardNumber) {
      fleetLimitersRef.current = cardDetails?.fleetLimiters;

      if (cardDetails?.contractNumber !== cardOptionsAccountNumber) {
        dispatch(getCardOptionsAction(cardDetails?.contractNumber, assignedGrandfatherClientNumber));
      }

      if (formId === 'card-edit-single-status' || isEditMultipleCard) {
        cardDetails.expiryDate = dayjs().add(60, 'month').tz('Australia/Sydney').format('YYMM');
      }

      setDefaultValues({
        ...getEditCardDetails(cardDetails),
        ...getFleetLimiterCodesObj(cardDetails?.fleetLimiters ?? []),
        ...(cardDetails?.cardNumber === defaultValues?.cardNumber && { ...defaultValues }),
        allProducts: cardDetails?.fleetLimiters?.every((item) => item.available === false || item.selected === true),
      });

      setVasRsaAccountCode(cardDetails?.accountDefault?.vasRsaAccountCode);
      setVasSnrAccountCode(cardDetails?.accountDefault?.vasSnrAccountCode);
      setVasFuelPayAccountCode(cardDetails?.accountDefault?.vasFuelPayAccountCode);
    }
  }, [isEditMultipleCard ? cardDetails : undefined]);

  useEffect(() => {
    if (vehicleTypes?.length === 0) {
      dispatch(getVehicleTypesAction());
    }

    if (cardDetails?.contractNumber !== cardOptionsAccountNumber && cardOptions?.length === 0) {
      dispatch(getCardOptionsAction(cardDetails?.contractNumber, assignedGrandfatherClientNumber));
    }

    if (formType === 'add') {
      // Using the functional-update form to avoid closing over stale defaultValues.
      setDefaultValues((prev) => ({
        ...prev,
        issueDate: dayjs().format(DATE_FORMAT),
        expiryDate: dayjs().add(maxExpiryMonths, 'month').tz('Australia/Sydney').format('MM/YY'),
        fuelPay: true,
        contractNumber: allAccounts?.length === 1 ? allAccounts[0].contractNumber : undefined,
      }));

      allAccounts?.length === 1 && handleAccountSelection(allAccounts[0].contractNumber);
    }

    return () => {
      dispatch(resetPlasticType());
      dispatch(resetCardAccountDefault());
      dispatch(getCardOptionsReset());
    };
    // allAccounts is the correct signal: re-run when the accounts list becomes available.
  }, [allAccounts]);

  useEffect(() => {
    if (isMounted.current && cardOptions) {
      cardOptionsRef.current = cardOptions;
    }
  }, [cardOptions]);

  useEffect(() => {
    if (isMounted.current && !isEmpty(accountDefaults) && formType === 'add') {
      fleetLimitersRef.current = accountDefaults?.fleetLimiters;

      if (plasticType?.id !== accountDefaults?.cardInfo?.plasticCardName) {
        dispatch(getPlasticType(accountDefaults?.cardInfo?.plasticCardName));
      }

      const modifiedDetails = {
        ...defaultValues,
        ...accountDefaults,
        allProducts: accountDefaults?.fleetLimiters?.every(
          (item) => item.available === false || item.selected === true
        ),
        ...getFleetLimiterCodesObj(accountDefaults?.fleetLimiters ?? []),
        roadsideAssist:
          (accountDefaults?.vasRsaAccountCode !== Vas.RSA_NC && accountDefaults?.vasRsaAccountCode !== Vas.RSA_YA) ??
          undefined,
        vehicleType:
          accountDefaults?.vasRsaAccountCode !== Vas.RSA_NC && accountDefaults?.vasRsaAccountCode !== Vas.RSA_YA
            ? vasClassifierMapping(accountDefaults?.vasRsaAccountCode)
            : undefined,
        serviceAndRepairs:
          (accountDefaults?.vasSnrAccountCode !== Vas.SNR_NC && accountDefaults?.vasSnrAccountCode !== Vas.SNR_YA) ??
          undefined,
        fuelPay:
          accountDefaults?.vasFuelPayAccountCode !== Vas.FP_NC && accountDefaults?.vasFuelPayAccountCode !== Vas.FP_YA,
      };

      setDefaultValues({ ...modifiedDetails });
      setModifiedCardDetailsRef.current?.(modifiedDetails);

      setVasRsaAccountCode(accountDefaults?.vasRsaAccountCode);
      setVasSnrAccountCode(accountDefaults?.vasSnrAccountCode);
      setVasFuelPayAccountCode(accountDefaults?.vasFuelPayAccountCode);
    }

    isMounted.current = true;
    // accountDefaults changing is the correct signal to re-run this initialisation.
  }, [accountDefaults]);

  return {
    defaultValues,
    setDefaultValues,
    fleetLimitersRef,
    cardOptionsRef,
    accountFavouritesData,
    vasRsaAccountCode,
    vasSnrAccountCode,
    vasFuelPayAccountCode,
    handleAccountSelection,
  };
};
